import React, { useEffect, useState } from 'react';
import { Link } from 'react-scroll';
import { useLocation } from 'react-router-dom';

export default function IncubateesPage() {
  const [activeTab, setActiveTab] = useState('cohort1');
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const location = useLocation();

  useEffect(() => {
    if (window.AOS) {
        window.AOS.init({ 
          duration: 800, 
          once: true, 
          easing: 'ease-out-cubic' 
        });
      }
  }, []);

  useEffect(() => {
    if (location.state?.cohort) {
      setActiveTab(location.state.cohort);
    }
  }, [location.state]);

  // Fetch startups when activeTab changes
  useEffect(() => {
    const fetchStartups = async () => {
      setLoading(true);
      setError(null);
      try {
        const cohortValue = activeTab === 'cohort1' ? 1 : 2;
        const response = await fetch(`https://tbido-website-api.azurewebsites.net/api/startups?cohort=${cohortValue}`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setStartups(data);
      } catch (err) {
        setError('Failed to load startups. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStartups();
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Gradient */}
      <section className="relative text-white overflow-hidden pt-20 sm:pt-24 md:pt-32 pb-32 sm:pb-40 md:pb-56 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/bg-2.png')" }}>
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            {/* Left Content */}
            <div data-aos="fade-right">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light mb-4 sm:mb-5 md:mb-6 leading-tight">
                <span className="text-white font-bold">Next Stop: Cohort 3 — Stay Tuned!</span>
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-6 sm:mb-7 md:mb-8 leading-relaxed">
                Applications for Cohort 2 have officially ended! Thank you to everyone who applied. Stay tuned for program updates and upcoming announcements for Cohort 3
              </p>
              
              {/* Stats Badge */}
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-7 md:mb-8 w-fit" data-aos="zoom-in" data-aos-delay="100">
                <div className="flex -space-x-2 sm:-space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white border-2 sm:border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                    <img src="/images/cybernest.png" alt="Logo 1" className="w-full h-full object-cover" />
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white border-2 sm:border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                    <img src="/images/ruvo.png" alt="Logo 2" className="w-full h-full object-cover" />
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white border-2 sm:border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                    <img src="/images/anehan.png" alt="Logo 3" className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="text-sm sm:text-base md:text-lg lg:text-xl font-extralight">9+ Total Teams Incubated!</span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4" data-aos="fade-up" data-aos-delay="200">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="bg-white px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-3.5 rounded-full font-medium text-sm sm:text-base hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer">
                  <span 
                    className="font-light bg-clip-text text-transparent"
                    style={{
                      backgroundImage: 'linear-gradient(to right, #6D0C22 0%, #0E386B 92%)'
                    }}
                  >
                    Contact Us
                  </span>
                </Link>
              </div>
            </div>

            {/* Right Side – ONE Large Image */}
            <div 
              className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl transform hover:scale-105 transition-transform duration-300"
              data-aos="fade-left"
              data-aos-delay="100"
            >
              <img
                src="/images/rightImage.png"
                alt="Cohort Showcase"
                className="w-full h-48 sm:h-64 md:h-80 lg:h-96 xl:h-[460px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
      <div id="cohorts-section"></div>

      {/* White Offset Container - Proper Negative Margin */}
      <section className="relative -mt-24 sm:-mt-32 md:-mt-44 lg:-mt-48 pb-12 sm:pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 md:p-12 lg:p-16" data-aos="fade-up">
            
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8 md:mb-10" data-aos="fade-down" data-aos-delay="100">
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(to right, #6D0C22 0%, #0E386B 92%)'
                }}
              >
                Introducing our Incubatees
              </h2>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-black max-w-2xl mx-auto leading-relaxed px-2">
                Bridging innovation with expertise, we provide our startups the tools and network to scale. 
                Explore the ventures driving change across our cohorts.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex border-b-2 border-gray-200 mb-8 sm:mb-10 md:mb-12">
              <button
                onClick={() => setActiveTab('cohort1')}
                className={`flex-1 py-3 sm:py-4 font-normal text-base sm:text-lg md:text-xl transition-all relative ${
                  activeTab === 'cohort1'
                    ? 'text-gray-900'
                    : 'text-gray-800 hover:text-gray-700'
                }`}
              >
                Cohort 1
                {activeTab === 'cohort1' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-red-900 rounded-t-full"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('cohort2')}
                className={`flex-1 py-3 sm:py-4 font-normal text-base sm:text-lg md:text-xl transition-all relative ${
                  activeTab === 'cohort2'
                    ? 'text-gray-900'
                    : 'text-gray-800 hover:text-gray-700'
                }`}
              >
                Cohort 2
                {activeTab === 'cohort2' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-red-900 rounded-t-full"></div>
                )}
              </button>
            </div>

            {/* Startup Cards Grid or Empty State */}
            {loading && <p className="text-center py-8">Loading startups...</p>}
            {error && <p className="text-center py-8 text-red-500">{error}</p>}
            {!loading && !error && startups.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-8 sm:mb-10 md:mb-12">
                {startups.map((startup, index) => (
                  <div
                    key={startup._id || index}
                    className="bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group flex flex-col"
                    data-aos="zoom-in"
                    data-aos-delay={200 + (index * 80)}
                  >
                    {/* Logo Container - Full Width */}
                    <div className="w-full h-36 sm:h-40 md:h-48 bg-white flex items-center justify-center p-4 sm:p-5 md:p-6">
                      <img
                        src={startup.logoUrl}
                        alt={startup.name}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Card Content */}
                    <div className="p-4 sm:p-5 md:p-6 text-center bg-gray-100 flex flex-col flex-grow">
                      {/* Name */}
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                        {startup.name}
                      </h3>

                      {/* Description */}
                      <p className="text-[10px] sm:text-xs text-gray-700 mb-4 sm:mb-5 md:mb-6 leading-relaxed flex-grow">
                        {startup.description}
                      </p>

                      {/* Button logic updated to show "Coming Soon" if website_url is missing */}
                      {startup.website_url ? (
                        <a 
                          href={startup.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative px-6 sm:px-7 md:px-8 py-1.5 sm:py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-sm group overflow-hidden mt-auto inline-block"
                          style={{
                            background: 'transparent',
                            border: '1px solid transparent',
                            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #6D0C22 0%, #0E386B 92%)',
                            backgroundOrigin: 'border-box',
                            backgroundClip: 'padding-box, border-box'
                          }}
                        >
                          <span 
                            className="font-normal text-[10px] sm:text-xs bg-clip-text text-transparent"
                            style={{
                              backgroundImage: 'linear-gradient(to right, #6D0C22 0%, #0E386B 92%)'
                            }}
                          >
                            View Website
                          </span>
                        </a>
                      ) : (
                        <button 
                          disabled
                          title="Stay Tuned!"
                          className="relative px-6 sm:px-7 md:px-8 py-1.5 sm:py-2 rounded-full font-semibold mt-auto inline-block opacity-50 cursor-not-allowed"
                          style={{
                            background: 'transparent',
                            border: '1px solid transparent',
                            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #6D0C22 0%, #0E386B 92%)',
                            backgroundOrigin: 'border-box',
                            backgroundClip: 'padding-box, border-box'
                          }}
                        >
                          <span 
                            className="font-normal text-[10px] sm:text-xs bg-clip-text text-transparent"
                            style={{
                              backgroundImage: 'linear-gradient(to right, #6D0C22 0%, #0E386B 92%)'
                            }}
                          >
                            Coming Soon
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && !error && startups.length === 0 && (
              <div className="text-center py-4 sm:py-6 mb-8 sm:mb-10 md:mb-12" data-aos="fade-up" data-aos-delay="200">
                <div className="max-w-xl mx-auto px-4">
                  {/* Illustration */}
                  <div className="mb-6 sm:mb-8 flex justify-center" data-aos="zoom-in" data-aos-delay="300">
                    <img
                      src="/images/incubatee-cohort2.png"
                      alt="Application Open"
                      className="w-48 sm:w-64 md:w-80 h-auto"
                    />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4" data-aos="fade-up" data-aos-delay="400">
                    Application for Second Cohort is still Open!
                  </h3>
                  
                  {/* Description */}
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed" data-aos="fade-up" data-aos-delay="500">
                    Your innovation deserves the right support. Join our second cohort and gain access to the tools, mentors, and network to scale your startup.
                  </p>
                </div>
              </div>
            )}

            {/* Footer Text */}
            <div className="text-center pt-6 sm:pt-8 md:pt-10 border-t-2 border-gray-200" data-aos="fade-up" data-aos-delay="900">
              <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">
                Innovation begins with people—and we're here to back every bold step.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}